import React, { useContext, useRef, useState } from 'react';

import { observer } from 'mobx-react-lite';

import TabsPanel from 'components/common/TabsPanel';
import { InputSearchIcon } from 'components/common/Icons/Icons';
import { RootStoreContext } from 'index';
import InputSearch from 'components/common/InputSearch/InputSearch';
import { useDebouncedCallback } from 'hooks/useDebounce';
import {
  ApplicationsInactiveStatus,
  ApplicationsStatusCategory,
  InactiveStatusTab,
  StatusCategoryTab,
} from 'components/applications/applications.enums';
import {
  DEFAULT_APPLICATIONS_TABLE_FILTERS,
  MIN_SYMBOLS_FOR_SEARCH,
  SEARCH_DEBOUNCE_VALUE,
  MAP_INACTIVE_STATUS_TAB_TO_INACTIVE_STATUS,
  MAP_INACTIVE_STATUS_TO_INACTIVE_STATUS_TAB,
} from 'components/applications/applications.constants';
import { ApplicationsPageFilters } from 'components/applications/applications.types';

import styles from './ApplicationsFilterPanel.module.scss';
import ApplicationsStatusGroup from './ApplicationsStatusGroup/ApplicationsStatusGroup';

const ApplicationsFilterPanel = () => {
  const inputRef = useRef(null);

  const { applicationsStore } = useContext(RootStoreContext);
  const { isLoading, filters } = applicationsStore;
  const [currentInactiveStatus, setCurrentInactiveStatus] = useState<InactiveStatusTab>(
    filters.inactiveStatus
      ? MAP_INACTIVE_STATUS_TO_INACTIVE_STATUS_TAB[filters?.inactiveStatus]
      : InactiveStatusTab.AllInactive,
  );

  const handleSearchAndFiltersUpdate = (newFilters: ApplicationsPageFilters) => {
    applicationsStore.updateFilters({ ...newFilters });
  };

  const debounceSearch = useDebouncedCallback(handleSearchAndFiltersUpdate, SEARCH_DEBOUNCE_VALUE);

  const changeCurrentTab = (newTab: StatusCategoryTab) => {
    setCurrentInactiveStatus(InactiveStatusTab.AllInactive);

    applicationsStore.updateFilters({
      ...filters,
      statusCategory:
        newTab === StatusCategoryTab.Inactive ? ApplicationsStatusCategory.Inactive : ApplicationsStatusCategory.Active,
      inactiveStatus: newTab === StatusCategoryTab.Inactive ? ApplicationsInactiveStatus.All : undefined,
      currentTab: newTab,
      pageIndex: 0,
    });
  };

  const searchHandler = (value: string) => {
    if (value.length >= MIN_SYMBOLS_FOR_SEARCH) {
      debounceSearch({ ...filters, search: value });
    }

    if (!value.length) {
      applicationsStore.updateFilters({ ...DEFAULT_APPLICATIONS_TABLE_FILTERS });
    }
  };

  const handleClearSearchValue = () => {
    applicationsStore.updateFilters({
      ...DEFAULT_APPLICATIONS_TABLE_FILTERS,
      currentTab: filters.currentTab,
    });
  };

  const handleStatusChange = (status: InactiveStatusTab) => {
    setCurrentInactiveStatus(status);

    applicationsStore.updateFilters({
      ...filters,
      statusCategory: ApplicationsStatusCategory.Inactive,
      inactiveStatus: MAP_INACTIVE_STATUS_TAB_TO_INACTIVE_STATUS[status],
      pageIndex: 0,
    });
  };

  return (
    <>
      <div className={styles.filterPanelContainer}>
        <div className={styles.searchInput}>
          <InputSearchIcon />
        </div>
        <InputSearch
          placeholder="Search by ID and Applicants Names"
          className={styles.searchInput}
          onSearch={searchHandler}
          onClear={handleClearSearchValue}
          ref={inputRef}
          isLoading={isLoading}
        />
        <TabsPanel
          disabled={isLoading}
          defaultTabId={applicationsStore.filters.currentTab || StatusCategoryTab.Active}
          tabs={[
            {
              id: StatusCategoryTab.Active,
              name: StatusCategoryTab.Active,
              onClick: () => changeCurrentTab(StatusCategoryTab.Active),
            },
            {
              id: StatusCategoryTab.Inactive,
              name: StatusCategoryTab.Inactive,
              onClick: () => changeCurrentTab(StatusCategoryTab.Inactive),
            },
          ]}
        />
      </div>
      {filters.currentTab === StatusCategoryTab.Inactive && (
        <ApplicationsStatusGroup isLoading={isLoading} status={currentInactiveStatus} onChange={handleStatusChange} />
      )}
    </>
  );
};

export default observer(ApplicationsFilterPanel);
